<script lang="ts" setup>
  import { h, type VNode } from 'vue'
  import { HugeiconsIcon } from '@hugeicons/vue'
  import AiSheetsIcon from '@hugeicons/core-free-icons/AiSheetsIcon'
  import Download04Icon from '@hugeicons/core-free-icons/Download04Icon'
  import SideBarButton from '~/components/layout/SideBarButton.vue'
  import { GlobeIcon, InventoryIcon, OperationsIcon, SettingsIcon } from '~/components/icons'
  import SidebarSettingsMenu from '~/components/layout/SidebarSettingsMenu.vue'
  import type { RouteLocationRaw } from 'vue-router'
  import PluginsIcon from '../icons/PluginsIcon.vue'
  import {
    hasReportCapability,
    useReportCapabilities,
    type ReportCapabilityKey
  } from '~/composables/useReportCapabilities'
  import { useReportDownloadsStore } from '~/stores/reportDownloadsStore'
  import type { HugeIcon } from '~/types/icons'
  import type { PermissionAction } from '~/types/permissions'
  import { isAllowedByMenuPermissions, type MenuPermissionRequirement } from '~/utils/permissions/menu-visibility'

  interface MenuItemBase {
    icon: Component
    label: string
    disabled?: boolean
    requiredPermissions?: MenuPermissionRequirement[]
    requiredCapabilities?: ReportCapabilityKey[]
  }

  export type MenuItem = MenuItemBase &
    ({ route: RouteLocationRaw; action?: never } | { route?: never; action: () => void })

  const route = useRoute()
  const { can } = usePermissions()
  const { capabilities } = useReportCapabilities()
  const authStore = useAuthStore()
  const downloadsStore = useReportDownloadsStore()

  const toMenuIcon =
    (icon: HugeIcon): Component =>
    (): VNode =>
      h(HugeiconsIcon, { icon, size: 20, strokeWidth: 1.5 })

  const allMenu: MenuItem[] = [
    {
      icon: GlobeIcon,
      label: 'menu.map',
      route: '/home'
    },
    {
      icon: OperationsIcon,
      label: 'menu.operations.title',
      route: '/operations',
      requiredPermissions: [
        { container: 'schedule', action: 'list' },
        { container: 'alert', action: 'list' }
      ]
    },
    {
      icon: InventoryIcon,
      label: 'menu.inventory.title',
      route: '/inventory',
      requiredPermissions: [
        { container: 'group', action: 'list' },
        { container: 'site', action: 'list' }
      ]
    },
    {
      icon: toMenuIcon(AiSheetsIcon),
      label: 'menu.reports',
      route: '/reports',
      requiredPermissions: [{ container: 'report', action: 'list' }]
    },
    {
      icon: PluginsIcon,
      label: 'menu.plugins.title',
      route: '/plugins',
      requiredPermissions: [{ container: 'plugin', action: 'list' }]
    }
  ]

  const downloadsItem: MenuItem = {
    icon: toMenuIcon(Download04Icon),
    label: 'menu.downloads',
    action: () => downloadsStore.open(),
    requiredPermissions: [{ container: 'report', action: 'list' }]
  }

  const settingsItem: MenuItem = {
    icon: SettingsIcon,
    label: 'menu.settings.title',
    route: '/settings',
    requiredPermissions: [{ container: 'user_management', action: 'list' }]
  }

  const isAllowedByCapabilities = (item: MenuItem): boolean =>
    (item.requiredCapabilities ?? []).every((key) => hasReportCapability(capabilities.value, key))

  const isAllowedByPermissions = (item: MenuItem): boolean =>
    isAllowedByMenuPermissions(item.requiredPermissions, {
      permissionsLoaded: authStore.permissionsLoaded,
      can: ({ container, action }) => can(container, action as PermissionAction).value
    })

  const isVisible = (item: MenuItem): boolean => isAllowedByCapabilities(item) && isAllowedByPermissions(item)

  const menu = computed(() => allMenu.filter(isVisible))

  const canSeeDownloads = computed(() => isVisible(downloadsItem))

  const canSeeSettings = computed(() => isVisible(settingsItem))

  const hoveredIndex = ref<number | null>(null)

  const selectedIndex = computed(() => {
    if (hoveredIndex.value !== null) {
      return hoveredIndex.value
    }
    return menu.value.findIndex((item) => route.matched.some((m) => m.path === item.route))
  })
</script>

<template>
  <div id="sidebar-container" class="h-full flex flex-col p-2 pr-0">
    <div class="sidebar">
      <div class="flex flex-col gap-16">
        <div class="flex justify-center pt-2 text-white">
          <img alt="logo" class="size-8" src="/assets/images/DOO-Icon.png" />
        </div>
        <div class="flex flex-col gap-2 relative">
          <SideBarButton
            v-for="(item, index) in menu"
            :key="item.label"
            :item="item"
            @mouseenter="hoveredIndex = index"
            @mouseleave="hoveredIndex = null"
          />
          <div
            v-if="selectedIndex >= 0"
            :style="`top: ${selectedIndex * 44}px`"
            class="absolute inset-0 overflow-hidden size-9 rounded-md transition-all duration-300 ease-in-out shadow-inner shadow-white group cursor-pointer pointer-events-none"
          >
            <div class="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-silver/70 to-transparent" />
            <div
              class="absolute top-0 left-0 w-full h-full bg-gradient-to-tl from-silver/20 to-transparent group-hover:opacity-100 opacity-0 transition-all duration-300 ease-in-out"
            />
            <div class="absolute inset-0 rounded-md border border-white/20" />
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <SideBarButton v-if="canSeeDownloads" :item="downloadsItem" />
        <SideBarButton v-if="canSeeSettings" :item="settingsItem" />
        <DeepDivider />
        <SidebarSettingsMenu />
      </div>
    </div>
  </div>
</template>

<style scoped>
  .sidebar {
    @apply bg-primary h-full p-2 rounded-lg flex flex-col justify-between gap-8 shadow-main w-fit;
  }
</style>
